import { Button } from '@tryhackme/thm-ui-components';
import { ArcherContainer, ArcherElement } from 'react-archer';
import { useNavigate } from 'react-router-dom';
import sal1Image from 'src/images/svgs/sal1-badge-new.svg';
import pt1Image from 'src/images/svgs/pt1-badge-new.svg';
import { useFeatureFlag } from 'src/common/hooks';
import { FeatureFlagName } from 'src/common/constants';
import { useFeatureIsOn } from 'src/common/growthbook';
import { useGetUserHasPassedCertificationQuery } from 'src/features/certifications/certification/certificate.slice';
import { CertificationType } from 'src/common/enums/certification';
import { CareerPathSelector } from '../career-path-selector/career-path-selector';
import { InfoCard } from '../info-card/info-card';
import { PathCard } from '../path-card/path-card';
import { useRoadmapHook } from '../../roadmap.helpers';
import { useGetPremiumRoadmapQuery } from '../../roadmap.slice';
import { RoadmapVariant } from '../../roadmap.types';
import { CertificateRoadmapCard } from '../certificate-roadmap-card/certificate-roadmap-card';
import { StyledArrowAnchor, StyledComputerScienceBasicsContainer, StyledCyberSecurityCareerSkillsContainer, StyledCyberSecurityFoundationsContainer, StyledGridPathCardGroup, StyledInfoCardWrapper, StyledPathCardGroup, StyledPremiumRoadmapCareerPaths, StyledPremiumRoadmapContainer, StyledWhatsNext } from './premium-roadmap.styles';
const useCertificationStatus = (type: CertificationType, isEnabled: boolean) => {
  const {
    data
  } = useGetUserHasPassedCertificationQuery({
    certificationExamType: type
  }, {
    skip: !isEnabled
  });
  return !!data?.data;
};
export const PremiumRoadmap = ({
  pathProgressData,
  currentPathCode,
  recommendedPath,
  variant,
  isSmall
}: {
  pathProgressData?: {
    pathId: string;
    progressPercentage: number;
  }[];
  currentPathCode?: string;
  recommendedPath?: string;
  variant: RoadmapVariant;
  isSmall?: boolean;
}) => {
  const navigate = useNavigate();
  const {
    data
  } = useGetPremiumRoadmapQuery();
  const {
    isMobile,
    arrowStyle,
    selectedOption,
    setSelectedOption
  } = useRoadmapHook();
  const getPathProgress = (pathId: string) => {
    const pathProgress = pathProgressData?.find(progress => progress.pathId === pathId)?.progressPercentage || 0;
    return Math.round(pathProgress);
  };
  const {
    enabled: isWebAppPentestingPathEnabled
  } = useFeatureFlag(FeatureFlagName.WEBAPP_PENTESTING_PATH);
  const isWelcomeVariant = variant === 'welcome';
  const isLearnVariant = variant === 'learn';
  const isShowSal1OnPremiumRoadmapEnabled = useFeatureIsOn('show-sal1-on-premium-roadmap');
  const isPT1ExamEnabled = useFeatureIsOn('pt1-exam');
  const hideAzurePath = useFeatureIsOn('hide-azure-content-search');
  const hasPassedSal1 = useCertificationStatus(CertificationType.SAL1, isShowSal1OnPremiumRoadmapEnabled);
  const hasPassedPt1 = useCertificationStatus(CertificationType.PT1, isPT1ExamEnabled);
  if (!data?.data) return null;
  return <ArcherContainer strokeColor="#3E475A" svgContainerStyle={{
    zIndex: 0
  }} noCurves data-sentry-element="ArcherContainer" data-sentry-component="PremiumRoadmap" data-sentry-source-file="premium-roadmap.tsx">
      <StyledPremiumRoadmapContainer data-sentry-element="StyledPremiumRoadmapContainer" data-sentry-source-file="premium-roadmap.tsx">
        <ArcherElement id="archer-premium-1" relations={[{
        targetId: 'archer-premium-2',
        targetAnchor: 'top',
        sourceAnchor: 'bottom',
        ...arrowStyle
      }]} data-sentry-element="ArcherElement" data-sentry-source-file="premium-roadmap.tsx">
          <StyledComputerScienceBasicsContainer data-sentry-element="StyledComputerScienceBasicsContainer" data-sentry-source-file="premium-roadmap.tsx">
            <InfoCard title="Computer Science Basics" subtitle="Acquire the basic computer science skills required to get started in cyber security." background data-sentry-element="InfoCard" data-sentry-source-file="premium-roadmap.tsx" />
          </StyledComputerScienceBasicsContainer>
        </ArcherElement>
        <PathCard path={data?.data?.presecurity} isCurrentPath={(currentPathCode || recommendedPath) === 'presecurity'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.presecurity?._id ?? '')} variant={variant} data-sentry-element="PathCard" data-sentry-source-file="premium-roadmap.tsx" />
        <ArcherElement id="archer-premium-2" relations={[{
        targetId: 'archer-premium-3',
        targetAnchor: 'top',
        sourceAnchor: 'bottom',
        ...arrowStyle
      }]} data-sentry-element="ArcherElement" data-sentry-source-file="premium-roadmap.tsx">
          <StyledCyberSecurityFoundationsContainer data-sentry-element="StyledCyberSecurityFoundationsContainer" data-sentry-source-file="premium-roadmap.tsx">
            <InfoCard title="Cyber Security Foundations" subtitle="Develop cyber security skills needed to enter any career in the industry." background data-sentry-element="InfoCard" data-sentry-source-file="premium-roadmap.tsx" />
          </StyledCyberSecurityFoundationsContainer>
        </ArcherElement>
        <StyledPathCardGroup data-sentry-element="StyledPathCardGroup" data-sentry-source-file="premium-roadmap.tsx">
          <PathCard path={data?.data?.cybersecurity101} isCurrentPath={(currentPathCode || recommendedPath) === 'cybersecurity101'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.cybersecurity101?._id ?? '')} variant={variant} data-sentry-element="PathCard" data-sentry-source-file="premium-roadmap.tsx" />
        </StyledPathCardGroup>
        {/*
          The archer-premium-3-spacer adds different spacing between
          the last element of the previous section and the cyber security career skills (the three columns).
          By default, react-archer balances the spaces in intersections
         */}
        <ArcherElement id="archer-premium-3" relations={[{
        targetId: 'archer-premium-3-spacer',
        targetAnchor: 'top',
        sourceAnchor: 'bottom',
        style: {
          endMarker: false,
          ...arrowStyle.style
        }
      }]} data-sentry-element="ArcherElement" data-sentry-source-file="premium-roadmap.tsx">
          <StyledCyberSecurityCareerSkillsContainer data-sentry-element="StyledCyberSecurityCareerSkillsContainer" data-sentry-source-file="premium-roadmap.tsx">
            {isMobile && <div>
                <CareerPathSelector selectedOption={selectedOption} handleSelection={setSelectedOption} variant={variant} />{' '}
              </div>}
            {!isMobile && <div>
                <InfoCard title="Cyber Security Career Skills" subtitle={<p>
                      {isWelcomeVariant ? 'Master the specific skills necessary for your career of interest.' : <>
                          Master the specific skills necessary for your career of interest. Not sure which career path
                          is right for you? Take our{' '}
                          <a href="https://tryhackme.com/r/careers/quiz" target="_blank" rel="noreferrer">
                            career quiz
                          </a>{' '}
                          to find out.
                        </>}
                    </p>} background />
              </div>}
          </StyledCyberSecurityCareerSkillsContainer>
        </ArcherElement>
        <ArcherElement id="archer-premium-3-spacer" relations={[{
        targetId: 'archer-premium-security-analyst-1',
        targetAnchor: 'top',
        sourceAnchor: 'bottom',
        style: {
          endMarker: isMobile && selectedOption?.value === 'security-analyst',
          ...arrowStyle.style
        }
      }, {
        targetId: 'archer-premium-pentester-1',
        targetAnchor: 'top',
        sourceAnchor: 'bottom',
        style: {
          endMarker: isMobile && selectedOption?.value === 'pentester',
          ...arrowStyle.style
        }
      }, {
        targetId: 'archer-premium-security-engineer-1',
        targetAnchor: 'top',
        sourceAnchor: 'bottom',
        style: {
          endMarker: isMobile && selectedOption?.value === 'security-engineer',
          ...arrowStyle.style
        }
      }]} data-sentry-element="ArcherElement" data-sentry-source-file="premium-roadmap.tsx">
          <div aria-hidden tabIndex={-1} style={isMobile ? undefined : {
          margin: '4.6rem'
        }} />
        </ArcherElement>
        <StyledPremiumRoadmapCareerPaths $isSmall={isSmall} data-sentry-element="StyledPremiumRoadmapCareerPaths" data-sentry-source-file="premium-roadmap.tsx">
          {(!isMobile || isMobile && selectedOption?.value === 'security-analyst') && <div>
              <ArcherElement id="archer-premium-security-analyst-1" relations={[{
            targetId: 'archer-premium-security-analyst-2',
            targetAnchor: 'top',
            sourceAnchor: 'bottom',
            style: {
              endMarker: false,
              ...arrowStyle.style
            }
          }]}>
                <StyledInfoCardWrapper $isSmall={isSmall}>
                  <InfoCard title="Security Analyst" isSmall={isSmall} subtitle={<p>
                        Get on the fast track to becoming a successful{' '}
                        {isWelcomeVariant ? <span>Security Analyst</span> : <a href="https://tryhackme.com/r/careers/cyber-security-analyst" target="_blank" rel="noreferrer">
                            Security Analyst
                          </a>}
                        .
                      </p>} background={false} />
                </StyledInfoCardWrapper>
              </ArcherElement>
              <StyledGridPathCardGroup isDisplayOnePathOnWelcomeEnabled>
                <PathCard path={data?.data?.soclevel1} isCurrentPath={(currentPathCode || recommendedPath) === 'soclevel1'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.soclevel1?._id ?? '')} variant={variant} isSmall={isSmall} />
                {isShowSal1OnPremiumRoadmapEnabled && <CertificateRoadmapCard certificate={{
              code: 'sal1',
              title: 'Security Analyst Level 1 (SAL1)',
              url: '/certification/security-analyst-level-1',
              type: 'Professional Certification',
              image: sal1Image
            }} concluded={hasPassedSal1} recommended={recommendedPath} isSmall={isSmall} certificationType={CertificationType.SAL1} />}
                <PathCard path={data?.data?.soclevel2} isCurrentPath={(currentPathCode || recommendedPath) === 'soclevel2'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.soclevel2?._id ?? '')} variant={variant} isSmall={isSmall} />
                {!hideAzurePath && <PathCard path={data?.data?.azuresecurity} isCurrentPath={(currentPathCode || recommendedPath) === 'azuresecurity'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.azuresecurity?._id ?? '')} variant={variant} isAddOn isSmall={isSmall} />}
              </StyledGridPathCardGroup>
              {isWelcomeVariant ? <ArcherElement id="archer-premium-security-analyst-2">
                  <StyledArrowAnchor style={{
              position: 'unset'
            }} />
                </ArcherElement> : <ArcherElement id="archer-premium-security-analyst-2" relations={[{
            targetId: 'whats-next',
            targetAnchor: 'top',
            sourceAnchor: 'bottom',
            style: {
              ...arrowStyle.style,
              endMarker: !isMobile || isMobile && selectedOption?.value === 'security-analyst'
            }
          }]}>
                  <StyledArrowAnchor />
                </ArcherElement>}
            </div>}
          {(!isMobile || isMobile && selectedOption?.value === 'pentester') && <div>
              <ArcherElement id="archer-premium-pentester-1" relations={[{
            targetId: 'archer-premium-pentester-2',
            targetAnchor: 'top',
            sourceAnchor: 'bottom',
            style: {
              endMarker: false,
              ...arrowStyle.style
            }
          }]}>
                <StyledInfoCardWrapper $isSmall={isSmall}>
                  <InfoCard isSmall={isSmall} title="Penetration Tester" subtitle={<p>
                        Level up and forge your path to victory as a{' '}
                        {isWelcomeVariant ? <span>Penetration Tester</span> : <a href="https://tryhackme.com/r/careers/penetration-tester" target="_blank" rel="noreferrer">
                            Penetration Tester
                          </a>}
                        .
                      </p>} background={false} />
                </StyledInfoCardWrapper>
              </ArcherElement>
              <StyledGridPathCardGroup isDisplayOnePathOnWelcomeEnabled>
                <PathCard isSmall={isSmall} path={data?.data?.jrpenetrationtester} isCurrentPath={(currentPathCode || recommendedPath) === 'jrpenetrationtester'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.jrpenetrationtester?._id ?? '')} variant={variant} />
                {isPT1ExamEnabled && <CertificateRoadmapCard certificate={{
              code: 'pt1',
              title: 'Jr. Penetration Tester (PT1)',
              url: '/certification/junior-penetration-tester',
              type: 'Professional Certification',
              image: pt1Image
            }} concluded={hasPassedPt1} recommended={recommendedPath} isSmall={isSmall} certificationType={CertificationType.PT1} />}
                <PathCard isSmall={isSmall} path={data?.data?.web} isCurrentPath={(currentPathCode || recommendedPath) === 'web'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.web?._id ?? '')} variant={variant} />
                {isWebAppPentestingPathEnabled && data?.data?.webapppentesting && <PathCard isSmall={isSmall} path={data?.data?.webapppentesting} isCurrentPath={(currentPathCode || recommendedPath) === 'webapppentesting'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.webapppentesting?._id ?? '')} variant={variant} />}

                {isWelcomeVariant ? <ArcherElement id="archer-premium-pentester-2">
                    <div>
                      <PathCard isSmall={isSmall} path={data?.data?.redteaming} isCurrentPath={(currentPathCode || recommendedPath) === 'redteaming'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.redteaming?._id ?? '')} variant={variant} />
                    </div>
                  </ArcherElement> : <ArcherElement id="archer-premium-pentester-2" relations={[{
              targetId: 'whats-next',
              targetAnchor: 'top',
              sourceAnchor: 'bottom',
              style: {
                ...arrowStyle.style,
                endMarker: !isMobile || isMobile && selectedOption?.value === 'pentester'
              }
            }]}>
                    <div>
                      <PathCard path={data?.data?.redteaming} isCurrentPath={(currentPathCode || recommendedPath) === 'redteaming'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.redteaming?._id ?? '')} variant={variant} isSmall={isSmall} />
                    </div>
                  </ArcherElement>}
              </StyledGridPathCardGroup>
            </div>}
          {(!isMobile || isMobile && selectedOption?.value === 'security-engineer') && <div>
              <ArcherElement id="archer-premium-security-engineer-1" relations={[{
            targetId: 'archer-premium-security-engineer-2',
            targetAnchor: 'top',
            sourceAnchor: 'bottom',
            style: {
              endMarker: false,
              ...arrowStyle.style
            }
          }]}>
                <StyledInfoCardWrapper $isSmall={isSmall}>
                  <InfoCard title="Security Engineer" isSmall={isSmall} subtitle={<p>
                        Navigate your journey to becoming a world-class{' '}
                        {isWelcomeVariant ? <span>Security Engineer</span> : <a href="https://tryhackme.com/r/careers/security-engineer" target="_blank" rel="noreferrer">
                            Security Engineer
                          </a>}
                        .
                      </p>} background={false} />
                </StyledInfoCardWrapper>
              </ArcherElement>
              <StyledGridPathCardGroup isDisplayOnePathOnWelcomeEnabled>
                <PathCard isSmall={isSmall} path={data?.data?.['security-engineer-training']} isCurrentPath={(currentPathCode || recommendedPath) === 'security-engineer-training'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.['security-engineer-training']?._id ?? '')} variant={variant} />
                <PathCard isSmall={isSmall} path={data?.data?.devsecops} isCurrentPath={(currentPathCode || recommendedPath) === 'devsecops'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.devsecops?._id ?? '')} variant={variant} />
                <PathCard isSmall={isSmall} path={data?.data?.attackinganddefendingaws} isCurrentPath={(currentPathCode || recommendedPath) === 'attackinganddefendingaws'} recommendedPath={recommendedPath} progress={getPathProgress(data?.data?.attackinganddefendingaws?._id ?? '')} variant={variant} isAddOn />
              </StyledGridPathCardGroup>
              {isWelcomeVariant ? <ArcherElement id="archer-premium-security-engineer-2">
                  <StyledArrowAnchor style={{
              position: 'unset'
            }} />
                </ArcherElement> : <ArcherElement id="archer-premium-security-engineer-2" relations={[{
            targetId: 'whats-next',
            targetAnchor: 'top',
            sourceAnchor: 'bottom',
            style: {
              ...arrowStyle.style,
              endMarker: !isMobile || isMobile && selectedOption?.value === 'security-engineer'
            }
          }]}>
                  <StyledArrowAnchor />
                </ArcherElement>}
            </div>}
        </StyledPremiumRoadmapCareerPaths>
        {(!isMobile && isLearnVariant || isMobile && selectedOption !== undefined && isLearnVariant) && <ArcherElement id="whats-next">
            <StyledWhatsNext>
              <p>What&apos;s next?</p>
              <p>Explore over 900 walkthrough and challenge rooms, with new content every week!</p>
              <Button variant="outlined" onClick={() => navigate('/hacktivities/search?page=1&kind=rooms&order=relevance')}>
                Explore more
              </Button>
            </StyledWhatsNext>
          </ArcherElement>}
      </StyledPremiumRoadmapContainer>
    </ArcherContainer>;
};